import {
  TASK_PRIORITIES,
  TASK_STATUSES,
  type TaskDetail,
  type TaskPriority,
  type TaskStatus,
} from '@fos/shared/contracts/work';
import { TASK_ORIGIN_LABELS, TASK_PRIORITY_LABELS, TASK_STATUS_LABELS } from '@fos/shared/labels';
import {
  Archive,
  ArchiveRestore,
  Bot,
  Calendar,
  ChevronDown,
  Clock,
  Compass,
  History,
  Link2,
  ListChecks,
  Mail,
  MessageSquareText,
  MoreHorizontal,
  Pencil,
  PenLine,
  Sparkles,
  UserRound,
} from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { appErrorCode } from '../../../lib/api';
import { useActiveSession } from '../../../lib/auth';
import { cn } from '../../../lib/cn';
import { formatDate, formatDateShort } from '../../../lib/format';
import { useRememberRecent } from '../../../lib/recent';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Avatar } from '../../../ui/Avatar';
import { ContextBanner } from '../../../ui/Blocks';
import { Button, IconButton } from '../../../ui/Button';
import { DateInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { Markdown } from '../../../ui/Markdown';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '../../../ui/Menu';
import { Mono } from '../../../ui/Mono';
import { Page } from '../../../ui/Page';
import { Breadcrumbs, type Crumb } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { Tooltip } from '../../../ui/Tooltip';
import { PoolChip, useServicePool } from '../../maintenance/components/ServicePool';
import { TOUCH_BUTTON, TOUCH_ICON } from '../../mobile';
import { ModulePage } from '../../modules/pages/ModulePage';
import { AddTimeDialog } from '../../time';
import { AiResultBlock, AssignAiDialog, TaskRuns, TaskRunsToggle } from '../components/ai';
import { ClientConversation } from '../components/ClientConversation';
import { EditTaskDialog } from '../components/NewTaskDialog';
import {
  contextLong,
  FROM_CLIENT_HINT,
  FromClientBadge,
  PriorityStatus,
  TaskChips,
  TaskStatusLabel,
} from '../components/parts';
import { TaskChecklist } from '../components/TaskChecklist';
import { TaskHistory } from '../components/TaskHistory';
import { TaskTimeSection, TaskTimeToggle } from '../components/TaskTime';
import { useTaskMutations, useTaskOptions, useToday } from '../hooks/useTasks';
import { dueInfo, dueToneClass, quickDueOptions } from '../lib/due';

/**
 * Karta zadania (Figma 04 Zadania / Karta zadania z wynikiem AI, 3020:2366): okruszki, tytuł, „Dodaj czas” i „⋯”,
 * linia meta (status, projekt/usługa, termin, wykonawca — każde zmienia się z menu), opis Markdown, checklista,
 * wynik pracy AI (Fala 5: `AiResultBlock` — ostatni przebieg zadania, „Odbierz wynik”, „Zobacz zmiany”), stopka: czas,
 * przebiegi AI zadania, historia i pochodzenie. „Zleć Claude” (ikona w nagłówku; Fala 9, wcześniej „Zleć AI”)
 * otwiera `AssignAiDialog`.
 */
export function TaskPage() {
  const access = useAccess();
  if (!access.can('tasks')) return <ModulePage module="tasks" />;
  return <TaskScreen />;
}

function TaskScreen() {
  const { id = '' } = useParams();
  const task = trpc.tasks.get.useQuery({ id }, { enabled: id !== '' });

  if (task.isPending) {
    return (
      <Page width="reading" className="max-w-[744px]">
        <div role="status" aria-label="Wczytywanie zadania" className="flex flex-col gap-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="mt-6 h-20 w-full" />
        </div>
      </Page>
    );
  }
  if (task.isError) {
    const notFound = appErrorCode(task.error) === 'NOT_FOUND';
    return (
      <Page width="reading" className="max-w-[744px]">
        <Breadcrumbs items={[{ label: 'Zadania', to: '/zadania' }]} />
        <Section className="mt-6">
          {notFound ? (
            <EmptyState
              icon={Compass}
              title="Nie ma takiego zadania"
              description="Mogło zostać przeniesione albo nie masz do niego dostępu."
              action={
                <Button asChild variant="secondary">
                  <Link to="/zadania">Wróć do zadań</Link>
                </Button>
              }
            />
          ) : (
            <ErrorState onRetry={() => void task.refetch()} retrying={task.isFetching} />
          )}
        </Section>
      </Page>
    );
  }
  return <TaskCard task={task.data} />;
}

const ORIGIN_ICONS = {
  manual: PenLine,
  message: Mail,
  meeting: Calendar,
  ai: Sparkles,
  mcp: Bot,
  proposal: ListChecks,
  // zgłoszenie z portalu klienta (17.10.2026; widok zgłoszeń — osobny zakres)
  client_portal: UserRound,
};

/**
 * Link do źródła zadania (MVP §6: pochodzenie i link do źródła; MVP §17 nr 3: zadania ze spotkania zachowują
 * źródło). Tylko typy z ekranem i modułem, który osoba widzi; reszta zostaje samą etykietą. Wiadomość (Fala 4) ma
 * osobny podpis „Z maila: Anna Kowalczyk, 01.10” z linkiem do wątku (`TaskDetail.originMessage`).
 */
function originHref(ref: TaskDetail['originRef'], can: (module: 'clients' | 'knowledge' | 'tasks') => boolean) {
  if (!ref) return null;
  if (ref.type === 'meeting' && can('clients')) return `/spotkania/${ref.id}`;
  if (ref.type === 'knowledge_item' && can('knowledge')) return `/wiedza/${ref.id}`;
  if (ref.type === 'task' && can('tasks')) return `/zadania/${ref.id}`;
  return null;
}

function TaskCard({ task }: { task: TaskDetail }) {
  const access = useAccess();
  useRememberRecent({
    type: 'task',
    id: task.id,
    title: task.title,
    subtitle: [`zadanie #${task.number}`, task.client?.name ?? task.project?.name].filter(Boolean).join(' · '),
    to: `/zadania/${task.id}`,
  });
  const today = useToday();
  const { update, archive, restore } = useTaskMutations();
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [dateDialog, setDateDialog] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const [timeListOpen, setTimeListOpen] = useState(false);
  const [runsOpen, setRunsOpen] = useState(false);
  const [assignAi, setAssignAi] = useState(false);
  const editable = task.canEdit && access.can('tasks', 'edit') && !task.archivedAt;
  // „Zleć Claude” (Fala 5, MVP §13; nazwa z Fali 9): moduł Agenci (Edycja) i edycja zadania — run zmienia stan AI
  const canAssignAi = editable && access.can('agents', 'edit');
  const canAddTime = access.can('time', 'edit') && !task.archivedAt;

  const patch = (
    fields: Partial<{
      status: TaskStatus;
      priority: TaskPriority;
      dueDate: string | null;
      assigneeUserId: string | null;
      title: string;
      descriptionMd: string | null;
    }>,
  ) => update.mutate({ id: task.id, version: task.version, ...fields });

  const crumbs: Crumb[] = [{ label: 'Zadania', to: '/zadania' }];
  if (task.client) {
    crumbs.push({ label: task.client.name, to: access.can('clients') ? `/klienci/${task.client.id}` : undefined });
  } else {
    crumbs.push({ label: task.project?.name ?? 'Wewnętrzne' });
  }

  const due = dueInfo(task.dueDate, today, { done: task.status === 'done' });
  // lifting 17.10.2026: klient i projekt jako chipy w nagłówku (klient w stałej barwie)
  const context = contextLong(task);
  // pula godzin usługi zadania (17.10.2026): chip „Pula: zostało 1 h 40 min” (aktywna usługa z pulą, bez kwot)
  const pool = useServicePool(task.service?.id);
  const OriginIcon = ORIGIN_ICONS[task.origin];
  const originLink = originHref(task.originRef, (m) => access.can(m));

  return (
    <Page width="reading" className="max-w-[744px] md:pt-14">
      <Breadcrumbs items={crumbs} />

      <header className="mt-3 flex flex-col gap-3 md:flex-row md:items-start md:gap-2">
        <TitleEditor title={task.title} editable={editable} onSave={(title) => patch({ title })} />
        <div className="flex shrink-0 items-center gap-2">
          {/* Ikona zamiast przycisku z tekstem: Figma 04 ma w nagłówku tylko „Dodaj czas”, a tytuł mieści się w linii. */}
          {canAssignAi && (
            <Tooltip content="Zleć Claude">
              <IconButton
                icon={Sparkles}
                label="Zleć Claude"
                size={32}
                iconSize={16}
                className={cn('relative', TOUCH_ICON)}
                onClick={() => setAssignAi(true)}
              />
            </Tooltip>
          )}
          {canAddTime && (
            <Button variant="secondary" icon={Clock} className={TOUCH_BUTTON} onClick={() => setTimeOpen(true)}>
              Dodaj czas
            </Button>
          )}
          <Menu>
            <MenuTrigger asChild>
              <IconButton
                icon={MoreHorizontal}
                label="Więcej akcji"
                size={32}
                iconSize={18}
                className={cn('relative', TOUCH_ICON)}
              />
            </MenuTrigger>
            <MenuContent align="end" className="w-60">
              {editable && (
                <MenuItem icon={Pencil} onSelect={() => setEditing(true)}>
                  Edytuj zadanie
                </MenuItem>
              )}
              <MenuItem
                icon={Link2}
                onSelect={() => {
                  void navigator.clipboard
                    ?.writeText(`${window.location.origin}/zadania/${task.id}`)
                    .then(() => toast.success('Skopiowano link do zadania'))
                    .catch(() => toast.error('Nie udało się skopiować linku'));
                }}
              >
                Kopiuj link
              </MenuItem>
              {editable && (
                <>
                  <MenuSeparator />
                  <MenuLabel>Priorytet</MenuLabel>
                  <MenuRadioGroup value={task.priority} onValueChange={(v) => patch({ priority: v as TaskPriority })}>
                    {TASK_PRIORITIES.map((p) => (
                      <MenuRadioItem key={p} value={p}>
                        {TASK_PRIORITY_LABELS[p]}
                      </MenuRadioItem>
                    ))}
                  </MenuRadioGroup>
                </>
              )}
              {task.canEdit && access.can('tasks', 'edit') && (
                <>
                  <MenuSeparator />
                  {task.archivedAt ? (
                    <MenuItem
                      icon={ArchiveRestore}
                      onSelect={() => restore.mutate({ id: task.id, version: task.version })}
                    >
                      Przywróć z archiwum
                    </MenuItem>
                  ) : (
                    <MenuItem
                      icon={Archive}
                      onSelect={() =>
                        archive.mutate(
                          { id: task.id, version: task.version },
                          {
                            onSuccess: (saved) => {
                              toast.success('Zadanie trafiło do archiwum', {
                                action: {
                                  label: 'Cofnij',
                                  onClick: () => restore.mutate({ id: saved.id, version: saved.version }),
                                },
                              });
                            },
                          },
                        )
                      }
                    >
                      Archiwizuj
                    </MenuItem>
                  )}
                </>
              )}
            </MenuContent>
          </Menu>
        </div>
      </header>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-body-s text-fg-secondary">
        <MetaMenu
          editable={editable}
          label={<TaskStatusLabel status={task.status} />}
          ariaLabel={`Status: ${TASK_STATUS_LABELS[task.status]}`}
        >
          <MenuRadioGroup value={task.status} onValueChange={(v) => patch({ status: v as TaskStatus })}>
            {/* „Zgłoszenie” tylko przy zgłoszeniu z portalu w „Do zrobienia” (reguła `tasks.move`, 17.10.2026) */}
            {TASK_STATUSES.filter(
              (s) =>
                s !== 'request' ||
                task.status === 'request' ||
                (task.origin === 'client_portal' && task.status === 'todo'),
            ).map((s) => (
              <MenuRadioItem key={s} value={s}>
                {TASK_STATUS_LABELS[s]}
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
        </MetaMenu>
        {context && (
          <MetaButton editable={editable} onClick={() => setEditing(true)} ariaLabel={`Powiązanie: ${context}. Zmień`}>
            <TaskChips task={task} className="flex-wrap" />
          </MetaButton>
        )}
        {pool && <PoolChip usage={pool} />}
        <MetaMenu
          editable={editable}
          ariaLabel={due ? `Termin: ${due.text}` : 'Ustaw termin'}
          label={
            due ? (
              <span className={dueToneClass(due)}>{due.text}</span>
            ) : (
              <span className="text-fg-muted">Bez terminu</span>
            )
          }
        >
          <MenuLabel>Termin{task.dueDate ? `: ${formatDate(task.dueDate)}` : ''}</MenuLabel>
          {quickDueOptions(today).map((o) => (
            <MenuItem key={o.value} onSelect={() => patch({ dueDate: o.value })}>
              {o.label}
              <span className="ml-2 text-caption text-fg-muted">{formatDateShort(o.value)}</span>
            </MenuItem>
          ))}
          <MenuItem icon={Calendar} onSelect={() => setDateDialog(true)}>
            Inna data…
          </MenuItem>
          {task.dueDate && (
            <>
              <MenuSeparator />
              <MenuItem onSelect={() => patch({ dueDate: null })}>Usuń termin</MenuItem>
            </>
          )}
        </MetaMenu>
        <AssigneeMenu task={task} editable={editable} onChange={(assigneeUserId) => patch({ assigneeUserId })} />
        <PriorityStatus priority={task.priority} />
        <FromClientBadge task={task} />
        <Mono className="text-caption text-fg-muted">#{task.number}</Mono>
      </div>

      {task.archivedAt && (
        <ContextBanner tone="info" icon={Archive} className="mt-6">
          <span className="flex flex-wrap items-center justify-between gap-2">
            Zadanie jest w archiwum. Przywróć je, aby wprowadzać zmiany.
            {task.canEdit && access.can('tasks', 'edit') && (
              <Button
                size="s"
                variant="secondary"
                icon={ArchiveRestore}
                className={TOUCH_BUTTON}
                loading={restore.isPending}
                onClick={() => restore.mutate({ id: task.id, version: task.version })}
              >
                Przywróć
              </Button>
            )}
          </span>
        </ContextBanner>
      )}

      {task.status === 'request' && !task.archivedAt && (
        // zgłoszenie z portalu klienta czeka na przyjęcie (17.10.2026): przyjęcie = „Do zrobienia”
        <ContextBanner tone="info" icon={MessageSquareText} className="mt-6">
          <span className="flex flex-wrap items-center justify-between gap-2">
            Zgłoszenie od klienta czeka na przyjęcie (w portalu klient widzi „Przyjęte do przeglądu”).
            {editable && (
              <Button
                size="s"
                variant="secondary"
                className={TOUCH_BUTTON}
                loading={update.isPending}
                onClick={() =>
                  update.mutate(
                    { id: task.id, version: task.version, status: 'todo' },
                    {
                      onSuccess: () => toast.success('Zgłoszenie przyjęte', { description: 'Jest w „Do zrobienia”.' }),
                    },
                  )
                }
              >
                Przyjmij
              </Button>
            )}
          </span>
        </ContextBanner>
      )}

      {/* treść karty w białej sekcji (DECISIONS 2026-10-07 „Nowy układ wizualny”); nagłówek i stopka na tle */}
      <Section className="mt-6 md:mt-8">
        <DescriptionEditor
          value={task.descriptionMd}
          editable={editable}
          onSave={(descriptionMd) => patch({ descriptionMd })}
          saving={update.isPending}
        />
        {task.clientRequest && (
          <ClientRequestNote request={task.clientRequest} title={task.title} descriptionMd={task.descriptionMd} />
        )}

        <div className="mt-5">
          <TaskChecklist task={task} editable={editable} />
        </div>

        {/* wynik pracy AI (Figma 04 „AI skończyło pracę”): ostatni przebieg zadania; bez przebiegów — nic */}
        <AiResultBlock taskId={task.id} aiState={task.aiState} className="mt-8" />
      </Section>

      {/* zgłoszenie z portalu klienta (17.10.2026): rozmowa widoczna dla klienta */}
      {task.origin === 'client_portal' && <ClientConversation taskId={task.id} />}

      <footer className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-body-s text-fg-muted">
        <TaskTimeToggle task={task} open={timeListOpen} onToggle={() => setTimeListOpen((v) => !v)} />
        <TaskRunsToggle taskId={task.id} open={runsOpen} onToggle={() => setRunsOpen((v) => !v)} />
        <button
          type="button"
          aria-expanded={historyOpen}
          aria-controls="historia-zadania"
          onClick={() => setHistoryOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-sm hover:text-fg focus-visible:outline-2 focus-visible:outline-accent max-md:min-h-11"
        >
          <History size={14} strokeWidth={1.75} aria-hidden />
          Historia
          <ChevronDown
            size={14}
            strokeWidth={1.75}
            aria-hidden
            className={cn('transition-transform', historyOpen && 'rotate-180')}
          />
        </button>
        {task.originMessage ? (
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <Mail size={14} strokeWidth={1.75} aria-hidden className="shrink-0" />
            <span className="min-w-0 truncate">
              <Link
                to={`/poczta/${task.originMessage.threadId}`}
                title={task.originMessage.subject}
                className="rounded-sm underline-offset-2 hover:text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
              >
                Z maila: {task.originMessage.from}
              </Link>
              , {formatDateShort(task.originMessage.sentAt)}
              {task.origin === 'ai' || task.origin === 'mcp' ? ` · ${TASK_ORIGIN_LABELS[task.origin]}` : ''}
            </span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            <OriginIcon size={14} strokeWidth={1.75} aria-hidden />
            {originLink ? (
              <Link
                to={originLink}
                className="rounded-sm underline-offset-2 hover:text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
              >
                {TASK_ORIGIN_LABELS[task.origin]}
              </Link>
            ) : (
              TASK_ORIGIN_LABELS[task.origin]
            )}
            , {formatDateShort(task.createdAt)}
            {task.createdBy ? ` · ${task.createdBy.name}` : ''}
          </span>
        )}
      </footer>
      {timeListOpen && access.can('time') && <TaskTimeSection task={task} />}
      {runsOpen && access.can('agents') && <TaskRuns taskId={task.id} />}
      {historyOpen && (
        <Section id="historia-zadania" aria-label="Historia" className="mt-4">
          <TaskHistory entries={task.history} />
        </Section>
      )}

      {editable && <EditTaskDialog open={editing} onOpenChange={setEditing} task={task} />}
      {canAssignAi && <AssignAiDialog task={task} open={assignAi} onOpenChange={setAssignAi} />}
      {canAddTime && (
        <AddTimeDialog
          open={timeOpen}
          onOpenChange={setTimeOpen}
          defaultTaskId={task.id}
          defaultClientId={task.client?.id ?? null}
          defaultProjectId={task.project?.id ?? null}
          onSaved={() => setTimeListOpen(true)}
        />
      )}
      <DueDateDialog
        open={dateDialog}
        onOpenChange={setDateDialog}
        value={task.dueDate}
        onSave={(dueDate) => patch({ dueDate })}
      />
    </Page>
  );
}

/** Element linii meta, który otwiera menu (status, termin, wykonawca); bez edycji — sam tekst. */
function MetaMenu({
  editable,
  label,
  ariaLabel,
  children,
}: {
  editable: boolean;
  label: ReactNode;
  ariaLabel: string;
  children: ReactNode;
}) {
  if (!editable) return <span className="inline-flex items-center">{label}</span>;
  return (
    <Menu>
      <MenuTrigger
        aria-label={ariaLabel}
        className="-mx-1.5 inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 hover:bg-elevated focus-visible:outline-2 focus-visible:outline-accent max-md:min-h-11"
      >
        {label}
      </MenuTrigger>
      <MenuContent className="w-60">{children}</MenuContent>
    </Menu>
  );
}

function MetaButton({
  editable,
  onClick,
  ariaLabel,
  children,
}: {
  editable: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: ReactNode;
}) {
  if (!editable) return <span>{children}</span>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="-mx-1.5 rounded-md px-1.5 py-0.5 hover:bg-elevated focus-visible:outline-2 focus-visible:outline-accent max-md:inline-flex max-md:min-h-11 max-md:items-center"
    >
      {children}
    </button>
  );
}

function AssigneeMenu({
  task,
  editable,
  onChange,
}: {
  task: TaskDetail;
  editable: boolean;
  onChange: (userId: string | null) => void;
}) {
  const session = useActiveSession();
  const options = useTaskOptions(editable);
  const label = task.assignee ? (
    <span className="inline-flex items-center gap-1.5">
      <Avatar initials={task.assignee.initials} color={task.assignee.avatarColor} />
      {task.assignee.name}
    </span>
  ) : (
    <span className="text-fg-muted">Bez wykonawcy</span>
  );
  const members = (options.data?.members ?? []).filter((m) => m.canTasks || m.userId === task.assignee?.userId);
  return (
    <MetaMenu editable={editable} label={label} ariaLabel={`Wykonawca: ${task.assignee?.name ?? 'brak'}. Zmień`}>
      <MenuLabel>Wykonawca</MenuLabel>
      {options.isPending ? (
        <p className="px-2.5 py-2 text-body-s text-fg-muted">Wczytywanie…</p>
      ) : (
        <MenuRadioGroup
          value={task.assignee?.userId ?? 'none'}
          onValueChange={(v) => onChange(v === 'none' ? null : v)}
        >
          {members.map((m) => (
            <MenuRadioItem key={m.userId} value={m.userId}>
              {m.userId === session.user.id ? `${m.name} (Ty)` : m.name}
            </MenuRadioItem>
          ))}
          <MenuSeparator />
          <MenuRadioItem value="none">Bez wykonawcy</MenuRadioItem>
        </MenuRadioGroup>
      )}
    </MetaMenu>
  );
}

function TitleEditor({
  title,
  editable,
  onSave,
}: {
  title: string;
  editable: boolean;
  onSave: (title: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const save = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== title) onSave(next);
    else setDraft(title);
  };
  if (editing) {
    return (
      <Input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') {
            setDraft(title);
            setEditing(false);
          }
        }}
        maxLength={300}
        aria-label="Tytuł zadania"
        className="h-10 flex-1 text-h4"
      />
    );
  }
  return (
    <h1 className="min-w-0 flex-1 text-h2 break-words text-fg md:text-h3">
      {editable ? (
        <button
          type="button"
          onClick={() => {
            setDraft(title);
            setEditing(true);
          }}
          className="rounded-md text-left hover:bg-elevated focus-visible:outline-2 focus-visible:outline-accent"
          aria-label={`${title}. Zmień tytuł`}
        >
          {title}
        </button>
      ) : (
        title
      )}
    </h1>
  );
}

function DescriptionEditor({
  value,
  editable,
  onSave,
  saving,
}: {
  value: string | null;
  editable: boolean;
  onSave: (value: string | null) => void;
  saving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const start = () => {
    setDraft(value ?? '');
    setEditing(true);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = draft.trim() ? draft : null;
    if (next !== value) onSave(next);
    setEditing(false);
  };
  if (editing) {
    return (
      <form onSubmit={submit} className="flex flex-col gap-2">
        <Field label="Opis" hint="Możesz użyć Markdown.">
          <Textarea autoFocus rows={6} value={draft} onChange={(e) => setDraft(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setEditing(false)}>
            Anuluj
          </Button>
          <Button type="submit" variant="secondary" loading={saving}>
            Zapisz opis
          </Button>
        </div>
      </form>
    );
  }
  if (!value) {
    return editable ? (
      <Button variant="ghost" size="s" icon={Pencil} className={cn('-ml-2.5', TOUCH_BUTTON)} onClick={start}>
        Dodaj opis
      </Button>
    ) : (
      <p className="text-body-s text-fg-muted">Bez opisu.</p>
    );
  }
  return (
    <div className="group relative">
      <Markdown>{value}</Markdown>
      {editable && (
        <Button variant="ghost" size="s" icon={Pencil} className={cn('mt-2 -ml-2.5', TOUCH_BUTTON)} onClick={start}>
          Edytuj opis
        </Button>
      )}
    </div>
  );
}

/**
 * Zgłoszenie z portalu klienta (przegląd portalu 17.10.2026): klient widzi w portalu swój tytuł i opis z chwili
 * zgłoszenia, więc zmiany na karcie zostają wewnętrzne. Po zmianie — podgląd wersji klienta.
 */
function ClientRequestNote({
  request,
  title,
  descriptionMd,
}: {
  request: { title: string; descriptionMd: string | null };
  title: string;
  descriptionMd: string | null;
}) {
  const changed = request.title !== title || (request.descriptionMd ?? '') !== (descriptionMd ?? '');
  return (
    <div className="mt-3 text-caption text-fg-muted">
      <p title={FROM_CLIENT_HINT}>
        Klient widzi w portalu swoją treść zgłoszenia — zmiany tytułu i opisu tutaj zostają wewnętrzne.
      </p>
      {changed && (
        <details className="mt-2">
          <summary className="cursor-pointer font-medium text-fg-secondary hover:text-fg max-md:py-3">
            Co widzi klient
          </summary>
          <div className="mt-2 rounded-md bg-soft px-3 py-2.5 text-body-s text-fg-secondary">
            <p className="font-medium text-fg">{request.title}</p>
            {request.descriptionMd ? (
              <div className="mt-1.5">
                <Markdown>{request.descriptionMd}</Markdown>
              </div>
            ) : (
              <p className="mt-1.5 text-fg-muted">Bez opisu.</p>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

function DueDateDialog({
  open,
  onOpenChange,
  value,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string | null;
  onSave: (value: string | null) => void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => {
    if (open) setDraft(value ?? '');
  }, [open, value]);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="s"
      title="Termin zadania"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              onSave(draft || null);
              onOpenChange(false);
            }}
          >
            Zapisz
          </Button>
        </>
      }
    >
      <Field label="Data">
        <DateInput value={draft} onValueChange={setDraft} autoFocus />
      </Field>
    </Dialog>
  );
}
